import React from 'react';
import { useTranslation } from 'react-i18next';

/* Say against do, as a picture. Hand-drawn SVG on purpose: the app ships no charting library and
   the bundle is already over the recommended size, so one screen is not worth ~100 kB of one.

   ★★ THE CROSSHAIR SITS ON THE COMPANY'S OWN FIGURES, NEVER ON 50/50. That is what ``say_do``
   means by a quadrant: above or below THE GROUP, not above or below the middle of an axis. A
   fixed centre would put a healthy company entirely in one corner and call three quadrants empty.

   ★★ AND THE VIEW IS SYMMETRIC AROUND THAT CROSSHAIR, which is what makes the four quadrants the
   same size. Drawing the full 0-100 on both axes put every dot in one small cloud — a company's
   participation rates live in the 70s and 80s while its survey scores live in the 40s and 50s, so
   most of the square is unreachable and the quadrants come out wildly lopsided for a reason that
   has nothing to do with the data. Scaling the same distance either side of the company fixes the
   look AND keeps the meaning: still "above or below the group", never "above or below 50".

   ★ The half-range is the furthest unit plus a margin, with a floor so a company whose units all
   sit on top of each other does not zoom into its own rounding noise. The axis ends are printed,
   because an axis that does not start at zero and says so is honest, and one that does not is not.

   ★ Area, not radius, follows headcount — a circle whose RADIUS doubles looks four times bigger,
   which would turn a department twice the size into one that dwarfs the rest.

   ★ A unit needs BOTH halves to be placed at all; one held back by a floor has no position on
   this picture. Those are counted underneath rather than quietly dropped. */

const PAD = { top: 18, right: 18, bottom: 40, left: 44 };
const W = 520;
const H = 420;

const HeatmapPlot = ({ rows, company, quadrantLabel }) => {
  const { t } = useTranslation();
  const plotted = rows.filter((r) => r.say !== null && r.say !== undefined && !r.say_hidden
    && r.do !== null && r.do !== undefined && !r.do_hidden);
  const missing = rows.length - plotted.length;

  const cx = company && company.say !== null && company.say !== undefined ? company.say : 50;
  const cy = company && company.do !== null && company.do !== undefined ? company.do : 50;

  // Same distance either side of the company, so the four quadrants are equal by construction.
  const MIN_HALF = 8;          // a tight cluster must not zoom into its own rounding
  const halfX = Math.max(MIN_HALF, ...plotted.map((r) => Math.abs(r.say - cx))) * 1.15;
  const halfY = Math.max(MIN_HALF, ...plotted.map((r) => Math.abs(r.do - cy))) * 1.15;

  const iw = W - PAD.left - PAD.right;
  const ih = H - PAD.top - PAD.bottom;
  const X = (v) => PAD.left + ((v - (cx - halfX)) / (2 * halfX)) * iw;
  const Y = (v) => PAD.top + ih - ((v - (cy - halfY)) / (2 * halfY)) * ih;
  // Printed ends, clamped into the real 0-100 domain: the axis may run past it for symmetry,
  // but a tick reading 104 would be a number that cannot exist.
  const tick = (v) => Math.round(Math.min(100, Math.max(0, v)));

  const biggest = Math.max(1, ...plotted.map((r) => r.headcount || 1));
  const R = (n) => 4 + 12 * Math.sqrt((n || 1) / biggest);   // area ∝ headcount

  const pad = 0.04;
  const corners = [
    { x: cx + halfX * (1 - pad), y: cy + halfY * (1 - pad), anchor: 'end', key: 'engaged' },
    { x: cx + halfX * (1 - pad), y: cy - halfY * (1 - pad), anchor: 'end', key: 'polite' },
    { x: cx - halfX * (1 - pad), y: cy + halfY * (1 - pad), anchor: 'start', key: 'quiet' },
    { x: cx - halfX * (1 - pad), y: cy - halfY * (1 - pad), anchor: 'start', key: 'struggling' },
  ];

  return (
    <div className="hm-plot">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('hmPlotTitle')}>
        <rect x={PAD.left} y={PAD.top} width={iw} height={ih} className="hm-plot__frame" />

        {/* the company's own position — the line every unit is judged against */}
        <line x1={X(cx)} y1={PAD.top} x2={X(cx)} y2={PAD.top + ih} className="hm-plot__split" />
        <line x1={PAD.left} y1={Y(cy)} x2={PAD.left + iw} y2={Y(cy)} className="hm-plot__split" />

        {corners.map((c) => (
          <text key={c.key} x={X(c.x)} y={Y(c.y)} textAnchor={c.anchor}
                dominantBaseline={c.y > cy ? 'hanging' : 'auto'} className="hm-plot__quad">
            {quadrantLabel(c.key)}
          </text>
        ))}

        {plotted.map((r) => (
          <g key={r.id} className="hm-plot__dot">
            <circle cx={X(r.say)} cy={Y(r.do)} r={R(r.headcount)}
                    style={{ fill: r.do_color || '#9ac6d3' }} />
            <title>{`${r.name} — ${t('hmSay')} ${Math.round(r.say)}, `
                   + `${t('hmDo')} ${Math.round(r.do)} (${r.headcount} ${t('hmPeople')})`}</title>
          </g>
        ))}

        <text x={PAD.left} y={H - 20} className="hm-plot__tick">{tick(cx - halfX)}</text>
        <text x={PAD.left + iw} y={H - 20} textAnchor="end"
              className="hm-plot__tick">{tick(cx + halfX)}</text>
        <text x={X(cx)} y={H - 20} textAnchor="middle"
              className="hm-plot__tick hm-plot__tick--split">{Math.round(cx)}</text>
        <text x={PAD.left + iw / 2} y={H - 6} textAnchor="middle"
              className="hm-plot__axis">{t('hmSay')} →</text>

        <text x={PAD.left - 6} y={PAD.top + ih} textAnchor="end"
              className="hm-plot__tick">{tick(cy - halfY)}</text>
        <text x={PAD.left - 6} y={PAD.top + 8} textAnchor="end"
              className="hm-plot__tick">{tick(cy + halfY)}</text>
        <text x={PAD.left - 6} y={Y(cy)} textAnchor="end"
              className="hm-plot__tick hm-plot__tick--split">{Math.round(cy)}</text>
        <text x={12} y={PAD.top + ih / 2} className="hm-plot__axis" textAnchor="middle"
              transform={`rotate(-90 12 ${PAD.top + ih / 2})`}>{t('hmDo')} →</text>
      </svg>

      <p className="hm-plot__note text-muted">
        {t('hmPlotNote')}
        {missing > 0 ? ` ${t('hmPlotMissing', { n: missing })}` : ''}
      </p>
    </div>
  );
};

export default HeatmapPlot;
